import { getFileApi } from '../file';

// ---------------------------------------------------------------------------
// Error types
// ---------------------------------------------------------------------------

export type FileDownloadErrorCode =
  | 'PRESIGNED_URL_FAILED'
  | 'DOWNLOAD_FAILED'
  | 'NO_DOWNLOAD_URL';

export interface FileDownloadError {
  code: FileDownloadErrorCode;
  message: string;
  status?: number;
  cause?: unknown;
}

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

export type FileDownloadResult =
  | { ok: true; blob: Blob; fileName: string; contentType: string }
  | { ok: false; error: FileDownloadError };

export type FileViewUrlResult =
  | { ok: true; url: string }
  | { ok: false; error: FileDownloadError };

// ---------------------------------------------------------------------------
// Consolidators
// ---------------------------------------------------------------------------

/**
 * Get a presigned URL for viewing a file (open in new tab).
 * Step 1 only — does not download the file content.
 */
export async function getFileViewUrl(fileId: string): Promise<FileViewUrlResult> {
  try {
    const response = await getFileApi({ fileId, requestDownloadUrl: true });
    const url = response.downloadUrl?.url;
    if (!url) {
      return {
        ok: false,
        error: { code: 'NO_DOWNLOAD_URL', message: 'Server returned no download URL' },
      };
    }
    return { ok: true, url };
  } catch (cause) {
    return {
      ok: false,
      error: { code: 'PRESIGNED_URL_FAILED', message: 'Failed to get view URL', cause },
    };
  }
}

/**
 * Rolled-up E2E file download:
 *   1. GET presigned URL from our API
 *   2. Download the file content from that URL
 */
export async function downloadFile(
  fileId: string,
  fileName: string,
): Promise<FileDownloadResult> {
  // Step 1 — get presigned URL
  const urlResult = await getFileViewUrl(fileId);
  if (!urlResult.ok) return urlResult;

  // Step 2 — download from presigned URL
  try {
    const response = await fetch(urlResult.url);
    if (!response.ok) {
      return {
        ok: false,
        error: {
          code: 'DOWNLOAD_FAILED',
          message: `Download failed with status ${response.status}`,
          status: response.status,
        },
      };
    }
    const blob = await response.blob();
    const contentType = response.headers.get('content-type') || 'application/octet-stream';
    return { ok: true, blob, fileName, contentType };
  } catch (cause) {
    return {
      ok: false,
      error: { code: 'DOWNLOAD_FAILED', message: 'Network error during download', cause },
    };
  }
}

// ---------------------------------------------------------------------------
// DOM utility
// ---------------------------------------------------------------------------

/**
 * Trigger a browser file-save dialog from a Blob.
 * Creates a temporary object URL, clicks a hidden <a>, then cleans up.
 */
export function triggerBrowserDownload(blob: Blob, fileName: string): void {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}
